/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { SagaIterator } from 'redux-saga';
import { call, fork, put, race, take } from 'redux-saga/effects';
import {
  RecordingOption,
  OptionUtils,
  RecordingOptionType,
  XError,
  AssetUtils,
  ActionsEventActionNames,
} from '@wit/vtv-x-foundation-lib';
import {
  SagaFunctions as SingleRecordingsSagaFunctions,
  ActionCreators as SingleRecordingsActionCreators,
  ActionType as SingleRecordingsActionTypes,
} from 'core/store/ducks/recordings/singleRecordings';
import { SagaFunctions as SeriesRecordingsSagaFunctions } from 'core/store/ducks/recordings/seriesRecordings';
import { addRecordingActionEvent } from '../../analytics/actionsEvent';

export default function* run(option: RecordingOption): SagaIterator {
  if (OptionUtils.isRecording(option) && option.asset) {
    switch (option.recordingOptionType) {
      case RecordingOptionType.RECORD_SINGLE: {
        const error: XError | undefined = yield call(
          SingleRecordingsSagaFunctions.addSingleRecordingSaga,
          option.asset
        );

        yield fork(addRecordingActionEvent, ActionsEventActionNames.RECORD, option.asset, error);
        if (error) {
          throw error;
        }
        break;
      }
      case RecordingOptionType.RECORD_SERIES:
        if (
          AssetUtils.hasValidSeriesId(option.asset) &&
          (AssetUtils.isEpg(option.asset) || AssetUtils.isRecording(option.asset))
        ) {
          const error: XError | undefined = yield call(
            SeriesRecordingsSagaFunctions.addBySerieIdSaga,
            option.asset.seriesId,
            option.asset.channelEpgId
          );

          yield fork(addRecordingActionEvent, ActionsEventActionNames.RECORD_SERIES, option.asset, error);
          if (error) {
            throw error;
          } else {
            yield put(SingleRecordingsActionCreators.loadBySeriesIds([option.asset.seriesId]));
            yield race([take(SingleRecordingsActionTypes.UPDATE), take(SingleRecordingsActionTypes.FAIL)]);
          }
        }
        break;
      case RecordingOptionType.CANCEL_EPISODE:
        if (option.singleRecording) {
          const error: XError | undefined = yield call(
            SingleRecordingsSagaFunctions.cancelSingleRecordingSaga,
            option.singleRecording
          );

          yield fork(
            addRecordingActionEvent,
            ActionsEventActionNames.CANCEL_EPISODE_RECORDING,
            option.singleRecording,
            error
          );
          if (error) {
            throw error;
          }
        }
        break;
      case RecordingOptionType.CANCEL_SINGLE:
        if (option.singleRecording) {
          const error: XError | undefined = yield call(
            SingleRecordingsSagaFunctions.cancelSingleRecordingSaga,
            option.singleRecording
          );

          yield fork(addRecordingActionEvent, ActionsEventActionNames.CANCEL_RECORDING, option.singleRecording, error);
          if (error) {
            throw error;
          }
        }
        break;
      case RecordingOptionType.CANCEL_SERIES:
        if (AssetUtils.hasValidSeriesId(option.asset) && option.seriesRecording) {
          const error: XError | undefined = yield call(
            SeriesRecordingsSagaFunctions.cancelSeriesRecordingSaga,
            option.seriesRecording
          );

          yield fork(addRecordingActionEvent, ActionsEventActionNames.CANCEL_SERIES_RECORDING, option.asset, error);
          if (error) {
            throw error;
          }
        }
        break;
      case RecordingOptionType.DELETE_SINGLE: {
        if (option.singleRecording) {
          const error: XError | undefined = yield call(
            SingleRecordingsSagaFunctions.deleteSingleRecordingSaga,
            option.singleRecording
          );

          yield fork(addRecordingActionEvent, ActionsEventActionNames.DELETE_RECORDING, option.singleRecording, error);
          if (error) {
            throw error;
          }
        }
        break;
      }
      case RecordingOptionType.DELETE_EPISODE: {
        if (option.singleRecording) {
          const error: XError | undefined = yield call(
            SingleRecordingsSagaFunctions.deleteSingleRecordingSaga,
            option.singleRecording
          );

          yield fork(
            addRecordingActionEvent,
            ActionsEventActionNames.DELETE_EPISODE_RECORDING,
            option.singleRecording,
            error
          );
          if (error) {
            throw error;
          }
        }
        break;
      }
      case RecordingOptionType.DELETE_SERIES: {
        if (
          AssetUtils.hasValidSeriesId(option.asset) &&
          (AssetUtils.isEpg(option.asset) || AssetUtils.isRecording(option.asset)) &&
          option.totalRecordings !== undefined &&
          option.totalProtected !== undefined
        ) {
          const error: XError | undefined = yield call(
            SingleRecordingsSagaFunctions.deleteBySeriesIdSaga,
            option.asset.seriesId,
            option.asset.channelEpgId
          );

          yield fork(addRecordingActionEvent, ActionsEventActionNames.DELETE_SERIES_RECORDING, option.asset, error);
          if (error) {
            throw error;
          }
        }
        break;
      }
      default:
        break;
    }
  }
}
